import { Dispatch, lazy, SetStateAction, useRef, useState } from "react";
import Layout from "./layouts/layout";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import UploadsList from "./screens/app/upload/uploadsList";
import SubmittedDocuments from "./screens/app/submittedDocuments/submittedDocuments";
import Home from "./screens/app/home/home";
import Login from "./screens/login";
import { PrivateRoute } from "./screens/privateRoute";
import { MfaSetupState, PspContextType } from "./config/types";
import { LanguageLocaleEnum, Mandate, MfaMethod, User } from "./api/openapi";
import {
  getActorId,
  getCookieConsent,
  getSelectedMandateId,
} from "./config/utils";
import Settings from "./screens/app/settings/settings";
import { useIsTablet } from "./config/hooks";
import ChangePasswordScreen from "./screens/app/settings/changePasswordScreen";
import UploadNewMobile from "./screens/app/upload/uploadNewMobile";
import ContactPersonMobile from "./screens/app/contactPersonMobile";
import MandatesMobile from "./screens/app/mandatesMobile";
import CalendarMobile from "./screens/app/home/calendarMobile/calendarMobile";
import { useRegisterSW } from "virtual:pwa-register/react";
import UpdatePrompt from "./screens/updatePrompt";
import { MicrosoftSsoLogin } from "./screens/microsoftSsoLogin";
import { PublicClientApplication } from "@azure/msal-browser";
import Messages from "./screens/app/home/messages/messages";
import ReadMessages from "./screens/app/home/messages/readMessage";
import PspDocuments from "./screens/app/pspDocuments/pspDocuments";
import { ResetPassword } from "./screens/resetPassword";
import CookiePolicy from "./cookies/cookiePolicy";
import PrivacyPolicy from "./privacy/PrivacyPolicy";
import { NotificationsMobile } from "./screens/app/home/notificationsMobile";
import Help from "./help/help";
import { client } from "./api/openapi/client.gen";
import API, { PSP_API_BASE_URL } from "./setupAxios";
// eslint-disable-next-line max-len
import TemplatesAndInstructions from "./screens/app/templatesAndInstructions/templatesAndInstructions";
import { Attendances } from "./screens/app/attendances";
import { AppProvidersWrapper } from "./AppProvidersWrapper";
// eslint-disable-next-line max-len
import { AttendanceMobileEmployeeScreen } from "./screens/app/attendances/attendanceMobile/attendanceMobileEmployeeScreen";
// eslint-disable-next-line max-len
import { AttendanceMobileEditForm } from "./screens/app/attendances/attendanceMobile/attendanceMobileEmployeeScreen/attendanceMobileEditForm";
// eslint-disable-next-line max-len
import { AttendanceVersionsMobile } from "./screens/app/attendances/attendanceMobile/attendanceVersionsMobile";
import { AttendancesLayout } from "./attendancesLayout";
import { DocumentsMobile } from "./screens/app/documentsMobile/documentsMobile";
import { NotFound404 } from "./screens/notFound404/notFound404";

const MessagesList = lazy(
  () => import("./screens/backoffice/messagesAdmin/messagesList"),
);
const NewMessage = lazy(
  () => import("./screens/backoffice/messagesAdmin/newMessage"),
);
const EditMessage = lazy(
  () => import("./screens/backoffice/messagesAdmin/editMessage"),
);
const UsersList = lazy(
  () => import("./screens/backoffice/usersAdmin/usersList"),
);
const EditUser = lazy(() => import("./screens/backoffice/usersAdmin/editUser"));
const MandatesList = lazy(
  () => import("./screens/backoffice/mandatesAdmin/mandatesList"),
);
const EditMandate = lazy(
  () => import("./screens/backoffice/mandatesAdmin/editMandate"),
);
const RightsList = lazy(
  () => import("./screens/backoffice/rightsAdmin/rightsList"),
);
const AddRight = lazy(
  () => import("./screens/backoffice/rightsAdmin/addRight"),
);
const EditRight = lazy(
  () => import("./screens/backoffice/rightsAdmin/editRight"),
);
const InboundDocumentsAdmin = lazy(
  () =>
    import("./screens/backoffice/inboundDocumentsAdmin/inboundDocumentsAdmin"),
);
const PspDocumentsAdmin = lazy(
  () => import("./screens/backoffice/pspDocumentsAdmin/pspDocumentsAdmin"),
);
const EmployeesList = lazy(() =>
  import("./screens/backoffice/employeesAdmin/employeesList").then((m) => ({
    default: m.EmployeesList,
  })),
);
const ExternalUsersList = lazy(() =>
  import("./screens/backoffice/externalUsersAdmin/externalUsersList").then(
    (m) => ({
      default: m.ExternalUsersList,
    }),
  ),
);
const AddExternalUser = lazy(() =>
  import("./screens/backoffice/externalUsersAdmin/externalUsersList/addExternalUser").then(
    (m) => ({ default: m.AddExternalUser }),
  ),
);
const TemplatesAndInstructionsAdmin = lazy(() =>
  // eslint-disable-next-line max-len
  import("./screens/backoffice/pspDocumentsAdmin/pspDocumentsAdmin/templatesAndInstructionsAdmin").then(
    (m) => ({ default: m.TemplatesAndInstructionsAdmin }),
  ),
);
const AdminPanel = lazy(() =>
  import("./screens/backoffice/adminPanel/adminPanel").then((m) => ({
    default: m.AdminPanel,
  })),
);

export default function App({
  instance: msalInstance,
}: {
  instance: PublicClientApplication;
}) {
  client.setConfig({ baseURL: PSP_API_BASE_URL, axios: API });
  const isTablet = useIsTablet();

  const [userState, setUserState] = useState<User>();
  const [langState, setLangState] = useState<LanguageLocaleEnum>("de");
  const [leftBarMobileOpen, setLeftBarMobileOpen] = useState<boolean>(false);
  const [selectedMandateId, setSelectedMandateId] = useState<string | null>(
    getSelectedMandateId(),
  );
  const [selectedMandate, setSelectedMandate] = useState<Mandate>();
  const [cookieConsentDialogOpen, setCookieConsentDialogOpen] =
    useState(!getCookieConsent());
  const [cookieConsentFunctionalOnly, setCookieConsentFunctionalOnly] =
    useState<boolean | undefined>(getCookieConsent()?.essentialOnly);

  const [mfaSetupState, setMfaSetupState] = useState<MfaSetupState>("closed");
  const onMfaMethodCreatedRef = useRef<(method: MfaMethod) => void>(null);
  const [actorId, setActorId] = useState<string | null>(getActorId());

  const { needRefresh, updateServiceWorker } = useRegisterSW();

  const pspContext: PspContextType = {
    initialized: true,
    lang: langState,
    setLang: setLangState,
    setUser: setUserState,
    user: userState,
    leftBarMobileOpen,
    setLeftBarMobileOpen,
    selectedMandateId,
    setSelectedMandateId,
    selectedMandate,
    setSelectedMandate,
    setCookieConsentDialogOpen,
    cookieConsentFunctionalOnly,
    setCookieConsentFunctionalOnly,
    msalInstance,
    mfaSetupState,
    setMfaSetupState,
    onMfaMethodCreatedRef,
    actorId,
    setActorId,
  };

  const routerInstance = router(
    pspContext,
    isTablet,
    msalInstance,
    needRefresh,
    updateServiceWorker,
    cookieConsentDialogOpen,
  );

  return <RouterProvider router={routerInstance} />;
}
const router = (
  pspContext: PspContextType,
  isTablet: boolean,
  msalInstance: PublicClientApplication,
  needRefresh: [boolean, Dispatch<SetStateAction<boolean>>],
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>,
  cookieConsentDialogOpen: boolean,
) =>
  createBrowserRouter([
    {
      element: (
        <AppProvidersWrapper
          pspContext={pspContext}
          msalInstance={msalInstance}
          cookieConsentDialogOpen={cookieConsentDialogOpen}
        />
      ),
      children: [
        // PUBLIC ROUTES
        {
          path: "/login",
          element: <Login />,
        },
        {
          path: "/sso-login",
          element: <MicrosoftSsoLogin />,
        },
        {
          path: "/reset-password",
          element: <ResetPassword />,
        },
        {
          path: "/cookie-policy",
          element: <CookiePolicy />,
        },
        {
          path: "/privacy-policy",
          element: <PrivacyPolicy />,
        },
        {
          path: "/help",
          element: <Help />,
        },

        // PROTECTED AREA
        {
          element: <PrivateRoute />,
          children: [
            {
              element: <Layout />,
              children: [
                { path: "/", element: <Navigate to="/home" replace /> },

                {
                  path: "/logged-in/privacy-policy",
                  element: <PrivacyPolicy />,
                },
                { path: "/logged-in/cookie-policy", element: <CookiePolicy /> },

                { path: "/home", element: <Home /> },
                { path: "/calendar", element: <CalendarMobile /> },
                { path: "/notifications", element: <NotificationsMobile /> },
                { path: "/contact-person", element: <ContactPersonMobile /> },
                {
                  path: "/templates-and-instructions",
                  element: <TemplatesAndInstructions />,
                },
                { path: "/switch-mandate", element: <MandatesMobile /> },
                {
                  path: "/submitted-documents/*",
                  element: <SubmittedDocuments />,
                },
                { path: "/psp-documents", element: <PspDocuments /> },
                {
                  path: "/documents/submitted/*",
                  element: <DocumentsMobile />,
                },
                { path: "/documents/psp/*", element: <DocumentsMobile /> },
                { path: "/upload", element: <UploadsList /> },
                { path: "/upload/new", element: <UploadNewMobile /> },
                { path: "/messages", element: <Messages /> },
                {
                  path: "/messages/read/:messageId",
                  element: <ReadMessages />,
                },
                {
                  element: <AttendancesLayout />,
                  children: [
                    {
                      path: "/attendances",
                      element: <Attendances />,
                    },
                    ...(!isTablet
                      ? [
                          {
                            path: "/attendances/versions",
                            element: <AttendanceVersionsMobile />,
                          },
                          {
                            path: "/attendances/:employeeMatricola/:employeeFiliale/:year/:month",
                            element: <AttendanceMobileEmployeeScreen />,
                          },
                          {
                            // eslint-disable-next-line max-len
                            path: "/attendances/:employeeMatricola/:employeeFiliale/:year/:month/:day",
                            element: <AttendanceMobileEditForm />,
                          },
                        ]
                      : []),
                  ],
                },

                { path: "/settings", element: <Settings /> },

                ...(!isTablet
                  ? [
                      {
                        path: "/settings/change-password",
                        element: <ChangePasswordScreen />,
                      },
                    ]
                  : []),

                // ADMIN
                {
                  path: "/admin",
                  children: [
                    { path: "users", element: <UsersList /> },
                    { path: "users/edit/:userId", element: <EditUser /> },
                    { path: "employees/edit/:userId", element: <EditUser /> },
                    {
                      path: "external-users/edit/:userId",
                      element: <EditUser />,
                    },

                    {
                      path: "external-users",
                      element: <ExternalUsersList />,
                    },
                    {
                      path: "external-users/add",
                      element: <AddExternalUser />,
                    },

                    { path: "employees", element: <EmployeesList /> },

                    { path: "mandates", element: <MandatesList /> },
                    {
                      path: "mandates/edit/:mandateId",
                      element: <EditMandate />,
                    },

                    { path: "rights", element: <RightsList /> },
                    { path: "rights/add", element: <AddRight /> },
                    { path: "rights/edit/:rightId", element: <EditRight /> },

                    {
                      path: "submitted-documents",
                      element: <InboundDocumentsAdmin />,
                    },

                    {
                      path: "psp-documents",
                      children: [
                        { index: true, element: <PspDocumentsAdmin /> },
                        {
                          path: "templates-and-instructions",
                          element: <TemplatesAndInstructionsAdmin />,
                        },
                      ],
                    },

                    { path: "messages", element: <MessagesList /> },
                    { path: "messages/new", element: <NewMessage /> },
                    {
                      path: "messages/edit/:messageId",
                      element: <EditMessage />,
                    },

                    { path: "admin-panel", element: <AdminPanel /> },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "/*",
          element: <NotFound404 />,
        },
      ],
    },

    // SW UPDATE FALLBACK ROUTE
    ...(needRefresh[0] &&
    !location.pathname.startsWith("/api") &&
    !location.pathname.startsWith("/flower")
      ? [
          {
            path: "*",
            element: <UpdatePrompt onUpdate={updateServiceWorker} />,
          },
        ]
      : []),
  ]);
